import React, { useEffect, useState, useRef, useMemo } from "react";
import { MaterialIcon } from "../components/UiComponents";
import { useAppContext } from "../utils/context/app-context";

import { LoadingSpinner } from "../components/UiComponents";

import { useNavigate, useLocation } from "react-router-dom";

export const Notifications = () => {
	const { state, api, dispatch } = useAppContext();
	const [showList, setShowList] = useState(false);
	const [loading, setLoading] = useState(true);
	const containerRef = useRef(null);
	const location = useLocation();

	// Check if user is currently inside a course route (e.g. /courses/232)
	const courseMatch = location.pathname.match(/^\/courses\/([^/]+)/);
	const currentCourseId = courseMatch ? courseMatch[1] : null;

	useEffect(() => {
		fetchNotifications();
	}, []);

	// Update notifications every 30 seconds
	useEffect(() => {
		const interval = setInterval(() => {
			fetchNotifications(true);
		}, 30000);
		return () => clearInterval(interval);
	}, []);

	// Close notifications dropdown when clicking outside
	useEffect(() => {
		function handleClickOutside(event) {
			if (containerRef.current && !containerRef.current.contains(event.target)) {
				setShowList(false);
			}
		}
		if (showList) {
			document.addEventListener("mousedown", handleClickOutside);
		}
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [showList]);

	// Filter notifications by course if inside a course route; otherwise show all
	const filteredNotifications = useMemo(() => {
		const all = state?.notifications || [];
		if (!currentCourseId) {
			return all;
		}
		return all.filter((n) => String(n.course_id) === String(currentCourseId));
	}, [state?.notifications, currentCourseId]);

	const count = filteredNotifications.length;

	async function fetchNotifications(refreshing = false) {
		if (state.offline) {
			setLoading(false);
			return;
		}

		if (!refreshing) {
			setLoading(true);
		}

		const { data, error } = await api.fetchData(`/notifications`);
		setLoading(false);

		if (!error && !data?.error) {
			dispatch({
				type: "SET_NOTIFICATIONS",
				payload: data.data || [],
			});
			setLoading(false);
		} else {
			setLoading(false);
		}
	}

	async function deleteNotifications(ids) {
		if (!ids || !ids.length) return;
		const ids_string = ids.join(",");
		await api.fetchData(`/notifications/delete`, "POST", {
			ids: ids_string,
		});
	}

	function handleDeleteNotification(notification) {
		dispatch({
			type: "DELETE_NOTIFICATION",
			payload: notification.id,
		});
		deleteNotifications([notification.id]);
	}

	function handleDeleteAll() {
		const ids = filteredNotifications.map((notification) => notification.id);
		if (!ids.length) return;

		const remaining = (state?.notifications || []).filter(
			(notification) => !ids.includes(notification.id)
		);
		dispatch({
			type: "SET_NOTIFICATIONS",
			payload: remaining,
		});
		deleteNotifications(ids);
		setShowList(false);
	}

	function handleShowList() {
		setShowList(!showList);
	}

	return (
		<div className="header-notifications" ref={containerRef}>
			<div
				className="header-notifications__button"
				role="button"
				onClick={handleShowList}>
				<div className="header-notifications__text">Notifications</div>
				<div className="header-notifications__icon">
					{loading ? (
						<div className="header-notifications__loading-spinner">
							<LoadingSpinner />
						</div>
					) : (
						<>
							<div className="header-notifications__bell">
								<MaterialIcon icon="notifications" />
							</div>
							{count > 0 && (
								<div className="header-notifications__count">
									{count}
								</div>
							)}
						</>
					)}
				</div>
			</div>
			{showList && !loading && (
				<NotificationsBox
					notifications={filteredNotifications}
					markRead={handleDeleteNotification}
					markAllRead={handleDeleteAll}
					close={() => setShowList(false)}
				/>
			)}
		</div>
	);
};

const NotificationsBox = ({ notifications, markRead, markAllRead, close }) => {
	if (
		!notifications ||
		(Array.isArray(notifications) && !notifications.length)
	) {
		return null;
	}

	return (
		<div className="notifications-box">
			<div className="notifications-box__mark-all-read">
				<button
					type="button"
					className="notifications-box__mark-all-read-button btn-reset"
					onClick={markAllRead}>
					<MaterialIcon icon="check" />
					Mark All Read
				</button>
			</div>
			<div className="notifications-box__list">
				{notifications.map((notification) => (
					<Notification
						key={notification.id}
						notification={notification}
						markRead={markRead}
						close={close}
					/>
				))}
			</div>
		</div>
	);
};

const Notification = ({ notification = null, markRead, close }) => {
	const navigate = useNavigate();

	if (!notification) {
		return null;
	}

	function buildContent() {
		const type = notification?.type;
		let content = "";

		switch (type) {
			case "registered":
			case "health_questionnaire":
			case "written_assessment":
				content = `<span>${notification.student_name}</span> ${notification.content}`;
				break;
			case "incident_witness":
				content = notification.content;
				break;
			default:
				break;
		}

		return <div dangerouslySetInnerHTML={{ __html: content }}></div>;
	}

	function handleClick() {
		const type = notification?.type;

		switch (type) {
			case "registered":
				close()
				navigate(`/courses/${notification.course_id}/attendance-register`, { replace: true })
				break;
			case "health_questionnaire":
				close()
				navigate(`/courses/${notification.course_id}/health-questionnaire/${notification.object_id}`, { replace: true })
				break;
			case "written_assessment":
				close()
				navigate(`/courses/${notification.course_id}/written-assessments/${notification.student_id}`, { replace: true })
				break;
			case "incident_witness":
				close()
				navigate(`/courses/${notification.course_id}/witness-incident/${notification.object_id}`, { replace: true })
				break;
			default:
				break;
		}
	}

	function handleMarkRead() {
		markRead(notification);
	}

	return (
		<div className="notification">
			<div className="notification__top">
				<div className="notification__icon">
					<MaterialIcon icon="description" />
				</div>
				<div className="notification__details">{buildContent()}</div>
			</div>
			<div className="notification-actions">
				<button
					type="button"
					className="notification-actions__button notification-actions__button--view btn-reset"
					onClick={handleClick}>
					<MaterialIcon icon="visibility" />
					View
				</button>
				<button
					type="button"
					className="notification-actions__button notification-actions__button--read btn-reset"
					onClick={handleMarkRead}>
					<MaterialIcon icon="check" />
					Mark Read
				</button>
			</div>
		</div>
	);
};
